import { useSearchParams, Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { ObangsaekStripe } from '@/components/editorial';
import CaseStudyCard from '@/components/financial/CaseStudyCard';
import { CASE_STUDIES, activeCategories, caseStudiesByCategory } from '@/content/financial/case-studies';
import { CATEGORY_LABELS, type CaseStudyCategory } from '@/content/financial/types';

const BASE_URL = 'https://koreanhub.net';

/**
 * The case study index, filterable by category.
 *
 * The filter mirrors /guides: a query parameter rather than a route, so the
 * prerendered /financial/case-studies HTML serves every filtered view. A
 * per-category route would need a prerendered page each and would be a soft 404
 * for any category that later emptied out.
 */
export default function CaseStudies() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('category');
  const categories = activeCategories() as CaseStudyCategory[];
  const active = raw && categories.includes(raw as CaseStudyCategory) ? (raw as CaseStudyCategory) : null;

  const visible = active ? caseStudiesByCategory(active) : CASE_STUDIES;

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Small-business case studies',
    url: `${BASE_URL}/financial/case-studies`,
    hasPart: CASE_STUDIES.map((c) => ({
      '@type': 'Article',
      headline: c.title,
      url: `${BASE_URL}/financial/case-studies/${c.slug}`,
      datePublished: c.publishedAt,
    })),
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Small-Business Case Studies — Real Numbers"
        description="Teardowns of micro-businesses with the full economics: capital in, monthly profit, hours per week, the execution sequence, and the failure modes. Every figure labelled with where it came from."
        path="/financial/case-studies"
        jsonLd={collectionSchema}
      />

      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <nav aria-label="Breadcrumb" className="mb-3 text-sm text-ink-500 dark:text-ink-400">
            <Link to="/financial" className="hover:underline">
              Financial
            </Link>
            <span className="mx-2" aria-hidden="true">
              /
            </span>
            <span className="text-ink-700 dark:text-ink-200">Case studies</span>
          </nav>
          <h1 className="text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            Case studies
          </h1>
          <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
            사례 연구
          </p>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            Capital in, monthly profit, hours per week, and the parts that genuinely go wrong. Every
            study is badged with how its numbers were obtained — most are modelled from published
            ranges rather than reported by an operator, and the badge says which.
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <nav className="mb-8 flex flex-wrap gap-2" aria-label="Filter case studies by category">
          <button
            type="button"
            onClick={() => setParams({})}
            aria-pressed={!active}
            className={
              !active
                ? 'rounded-full bg-cheong-500 px-4 py-2 text-sm font-medium text-white dark:bg-cheong-dark'
                : 'rounded-full bg-ink-100 px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-200 dark:bg-white/10 dark:text-ink-200'
            }
          >
            All studies
          </button>
          {categories.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setParams({ category: id })}
              aria-pressed={active === id}
              className={
                active === id
                  ? 'rounded-full bg-cheong-500 px-4 py-2 text-sm font-medium text-white dark:bg-cheong-dark'
                  : 'rounded-full bg-ink-100 px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-200 dark:bg-white/10 dark:text-ink-200'
              }
            >
              {CATEGORY_LABELS[id].label}
            </button>
          ))}
        </nav>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((study) => (
            <CaseStudyCard key={study.slug} study={study} />
          ))}
        </div>

        {visible.length === 0 && (
          <p className="py-16 text-center text-ink-500 dark:text-ink-400">
            No case studies in this category yet.{' '}
            <Link
              to="/financial/case-studies"
              className="font-medium text-cheong-500 underline dark:text-cheong-dark"
            >
              See all case studies
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
